import {
  calcImplSumAmount,
  correctedPriceNoVat,
  priceNoVat,
  priceSumNoVat,
  sumNoVatByUnitWithCorrect,
} from '../../../utils/calculation'
import {
  buildCell,
  buildMainHeaderCell,
  buildMergedHeaderCell,
  buildProjectName,
  buildReportDate,
  buildSubHeaderCell,
} from '../report-utils'
import uniq from 'lodash/uniq'

const ExcelJS = require('exceljs')
const FileSaver = require('file-saver')

const buildHeader = (worksheet, start = 1) => {
  buildMergedHeaderCell(worksheet, 'A', 'Позиция', start)
  buildMergedHeaderCell(worksheet, 'B', 'Категория', start)
  buildMergedHeaderCell(worksheet, 'C', 'Ед. изм.', start, 15)

  buildMainHeaderCell(worksheet, 4, 6, 'Смета', start)
  buildSubHeaderCell(worksheet, 4, 'Количество', 20, start)
  buildSubHeaderCell(worksheet, 5, 'Цена за ед, без НДС', 25, start)
  buildSubHeaderCell(worksheet, 6, 'Сумма, без НДС', 25, start)

  buildMainHeaderCell(worksheet, 7, 15, 'Факт', start)
  buildSubHeaderCell(worksheet, 7, 'Номер заявки', 20, start)
  buildSubHeaderCell(worksheet, 8, 'Кол-во', 20, start)
  buildSubHeaderCell(worksheet, 9, 'Корректировка кол-ва', 30, start)
  buildSubHeaderCell(worksheet, 10, 'Ед.изм.', 20, start)
  buildSubHeaderCell(worksheet, 11, 'Цена за ед. без НДС', 25, start)
  buildSubHeaderCell(worksheet, 12, 'Кор-ка стоимости', 25, start)
  buildSubHeaderCell(worksheet, 13, 'Сумма по позиции без НДС', 30, start)
  buildSubHeaderCell(worksheet, 14, 'Всего кол-во', 20, start)
  buildSubHeaderCell(worksheet, 15, 'Сумма, руб. без НДС', 25, start)

  buildMainHeaderCell(worksheet, 16, 18, 'Выполнено по актам', start)
  buildSubHeaderCell(worksheet, 16, 'Всего кол-во', 20, start)
  buildSubHeaderCell(worksheet, 17, 'Цена за ед, без НДС', 25, start)
  buildSubHeaderCell(worksheet, 18, 'Сумма, руб. без НДС', 25, start)
}

const exportToXLS = async (data, project, hidden = true, sheetName = 'Отчет') => {
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet(sheetName)

  buildReportDate(worksheet, 2)

  buildProjectName(worksheet, project, 4)

  const start = 6

  buildHeader(worksheet, start)

  data.reduce((nextIndex, item) => {
    const name = item?.estimate?.name
    const category = item?.estimate?.category
    const unit = item?.estimate?.unit
    const amount = item?.estimate?.amount
    const price = item?.estimate?.price
    const sum = item?.estimate?.price * item?.estimate?.amount
    const implementations = item?.implementations
    const acts = item?.acts
    const appNumbers = implementations.map((implementation, index, arr) => implementation?.applicationId)
    const counts = implementations.map((implementation, index, arr) =>
      implementation?.actualAmount !== null && implementation?.actualAmount >= 0
        ? implementation?.actualAmount
        : implementation?.amount
    )
    const units = implementations.map((implementation, index, arr) => implementation?.unit)
    const corrects = implementations.map((implementation, index, arr) => implementation?.correctedAmount)
    const pricesNoVat = implementations.map((implementation, index, arr) => priceNoVat(implementation))
    const sumsNoVat = implementations.map((implementation, index, arr) => priceSumNoVat(implementation))
    const sumCorrectedPrice = implementations.map((implementation, index, arr) => correctedPriceNoVat(implementation))

    const sumObject = sumNoVatByUnitWithCorrect(implementations)
    const finalSum = Object.values(sumObject).map((sumObjectItem, index, arr) => sumObjectItem.sum)

    const finalAmount = calcImplSumAmount(implementations)

    const actsSum = acts.reduce((acc, act) => acc + act.amount, 0)
    const uniqueActPrices = uniq(acts.map((item) => priceNoVat(item)))
    const actsSumNoVat = acts.reduce((acc, act) => acc + priceSumNoVat(act), 0)

    const highestIndex = implementations?.length === 0 ? 1 : implementations?.length
    const newIndex = implementations ? nextIndex + highestIndex : nextIndex + 1

    if (highestIndex > 1) {
      worksheet.mergeCells(nextIndex, 1, newIndex - 1, 1)
      worksheet.mergeCells(nextIndex, 2, newIndex - 1, 2)
      worksheet.mergeCells(nextIndex, 3, newIndex - 1, 3)
      worksheet.mergeCells(nextIndex, 4, newIndex - 1, 4)
      worksheet.mergeCells(nextIndex, 5, newIndex - 1, 5)
      worksheet.mergeCells(nextIndex, 6, newIndex - 1, 6)
      worksheet.mergeCells(nextIndex, 14, newIndex - 1, 14)
      worksheet.mergeCells(nextIndex, 16, newIndex - 1, 16)
      worksheet.mergeCells(nextIndex, 17, newIndex - 1, 17)
      worksheet.mergeCells(nextIndex, 18, newIndex - 1, 18)
    }

    const mergeCells = (rows, cellIndex) => {
      if (rows.length <= 1 && highestIndex > 1) {
        worksheet.mergeCells(nextIndex, cellIndex, newIndex - 1, cellIndex)
      }
    }

    mergeCells(finalSum, 15)

    const buildRows = (rows, cellIndex) =>
      rows.forEach((row, index) => buildCell(worksheet, row, cellIndex, nextIndex + index))

    buildCell(worksheet, name, 1, nextIndex)
    buildCell(worksheet, category, 2, nextIndex)
    buildCell(worksheet, unit, 3, nextIndex)
    buildCell(worksheet, amount, 4, nextIndex)
    buildCell(worksheet, price, 5, nextIndex)
    buildCell(worksheet, sum, 6, nextIndex)
    buildRows(appNumbers, 7)
    buildRows(counts, 8)
    buildRows(corrects, 9)
    buildRows(units, 10)
    buildRows(pricesNoVat, 11)
    buildRows(sumCorrectedPrice, 12)
    buildRows(sumsNoVat, 13)
    buildCell(worksheet, finalAmount, 14, nextIndex)
    buildRows(finalSum, 15)
    buildCell(worksheet, actsSum, 16, nextIndex)
    buildRows(uniqueActPrices, 17)
    buildCell(worksheet, actsSumNoVat, 18, nextIndex)

    return newIndex
  }, start + 2)

  worksheet.getColumn(7).hidden = hidden
  worksheet.getColumn(8).hidden = hidden
  worksheet.getColumn(9).hidden = hidden
  worksheet.getColumn(10).hidden = hidden
  worksheet.getColumn(11).hidden = hidden
  worksheet.getColumn(12).hidden = hidden
  worksheet.getColumn(13).hidden = hidden

  const buffer = await workbook.xlsx.writeBuffer()
  FileSaver.saveAs(new Blob([buffer]), 'sum_result.xlsx')
}

export default exportToXLS
